import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { AppButton, AppListPage } from '../../components/design-system';
import { EnhancedTable, type TableAction, type TableColumn } from '../../components/ui';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import { can, PRODUCT_NAMES } from '@cnapse/shared';
import * as dt from '@cnapse/shared/data.types.js';
import type { CreateWaferImagingTemplateRequest } from '@cnapse/shared/schemas.js';
import {
  waferImagingTemplatesService,
  type WaferImagingTemplateListItem,
} from '../../services/WaferImagingTemplatesService';
import { invalidateWaferImagingTemplatesCache } from '../../utils/jobWizardDataCache';
import { appNavigate } from '../../utils/appNavigate';
import { WaferImagingTemplateFormDialog } from '../../components/efem/templates/WaferImagingTemplateFormDialog';
import { formatLightingModesSummary } from '../../utils/waferImagingTemplateDisplay';
import {
  formatGatewayCompatibilityLabel,
  useGatewayCameraRolesSnapshot,
} from '../../hooks/useGatewayCameraRolesSnapshot';

interface ScanTemplatesPageProps {
  currentUserRole: dt.ALL_USER_ROLES;
}

export default function ScanTemplatesPage({ currentUserRole }: ScanTemplatesPageProps) {
  const [templates, setTemplates] = useState<WaferImagingTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WaferImagingTemplateListItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WaferImagingTemplateListItem | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const canManage = can(currentUserRole, 'scan_template:write');

  const { snapshot: gatewaySnapshot, loading: gatewaySnapshotLoading } =
    useGatewayCameraRolesSnapshot();

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await waferImagingTemplatesService.listWaferImagingTemplates();
      setTemplates(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load scan templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  useAppToolbar({});

  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(term) ||
        t.waferSize.toLowerCase().includes(term) ||
        t.idSide.toLowerCase().includes(term),
    );
  }, [templates, searchTerm]);

  const pagedTemplates = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredTemplates.slice(start, start + pageSize);
  }, [filteredTemplates, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (template: WaferImagingTemplateListItem) => {
    setEditingTemplate(template);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleSave = async (data: CreateWaferImagingTemplateRequest) => {
    setSubmitting(true);
    try {
      if (editingTemplate) {
        await waferImagingTemplatesService.updateWaferImagingTemplate(editingTemplate.id, data);
        setSnackbar({ open: true, message: 'Template updated', severity: 'success' });
      } else {
        await waferImagingTemplatesService.createWaferImagingTemplate(data);
        setSnackbar({ open: true, message: 'Template created', severity: 'success' });
      }
      invalidateWaferImagingTemplatesCache();
      handleCloseDialog();
      await loadTemplates();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Failed to save template',
        severity: 'error',
      });
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await waferImagingTemplatesService.deleteWaferImagingTemplate(deleteTarget.id);
      invalidateWaferImagingTemplatesCache();
      setSnackbar({ open: true, message: 'Template deleted', severity: 'success' });
      setDeleteTarget(null);
      await loadTemplates();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Failed to delete template',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const columns: TableColumn<WaferImagingTemplateListItem>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.2,
        minWidth: 180,
        renderCell: (row) => (
          <Typography variant="body2" fontWeight="medium">
            {row.name}
          </Typography>
        ),
      },
      {
        field: 'waferSize',
        headerName: 'Size · Side',
        minWidth: 120,
        renderCell: (row) => (
          <Typography variant="body2">
            {row.waferSize} · {row.idSide}
          </Typography>
        ),
      },
      {
        field: 'markOffsetDeg',
        headerName: 'Mark offset',
        minWidth: 100,
        renderCell: (row) => <Typography variant="body2">{row.markOffsetDeg}°</Typography>,
      },
      {
        field: 'startingLightingModes',
        headerName: 'Lighting modes',
        flex: 1.5,
        minWidth: 200,
        renderCell: (row) => (
          <Typography variant="body2" noWrap title={formatLightingModesSummary(row.startingLightingModes)}>
            {formatLightingModesSummary(row.startingLightingModes)}
          </Typography>
        ),
      },
      {
        field: 'isSystemTemplate',
        headerName: 'Type',
        minWidth: 90,
        renderCell: (row) => (
          <Chip
            size="small"
            label={row.isSystemTemplate ? 'System' : 'Custom'}
            color={row.isSystemTemplate ? 'default' : 'primary'}
            variant={row.isSystemTemplate ? 'outlined' : 'filled'}
          />
        ),
      },
      {
        field: 'gatewayCompatibility',
        headerName: 'Gateways',
        minWidth: 130,
        renderCell: (row) => (
          <Typography
            variant="body2"
            color={
              gatewaySnapshot &&
              formatGatewayCompatibilityLabel(row, gatewaySnapshot) === 'No matching camera'
                ? 'warning.main'
                : 'text.primary'
            }
          >
            {gatewaySnapshotLoading ? '…' : formatGatewayCompatibilityLabel(row, gatewaySnapshot)}
          </Typography>
        ),
      },
    ],
    [gatewaySnapshot, gatewaySnapshotLoading],
  );

  const rowActions: TableAction<WaferImagingTemplateListItem>[] = useMemo(() => {
    if (!canManage) return [];
    return [
      {
        label: 'Edit',
        icon: <EditIcon fontSize="small" />,
        onClick: handleOpenEdit,
      },
      {
        label: 'Test in Wafer ID Acquire',
        onClick: (row) => {
          appNavigate(`/operations/wafer-id-acquire?templateId=${row.id}`);
        },
      },
      {
        label: 'Delete',
        icon: <DeleteIcon fontSize="small" />,
        color: 'error',
        visible: (row) => !row.isSystemTemplate,
        onClick: (row) => setDeleteTarget(row),
      },
    ];
  }, [canManage]);

  if (loading && templates.length === 0) {
    return (
      <AppListPage title="Scan Templates">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </AppListPage>
    );
  }

  return (
    <AppListPage
      title="Scan Templates"
      description={`Presets for ${PRODUCT_NAMES.AXON} wafer ID capture — size, ID side, mark offset, and starting light modes.`}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only access. Contact an administrator or lead to edit scan templates.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Templates appear in scan jobs only when a gateway has a wafer ID camera bound for the same
        wafer size and ID side. Configure cameras in Axon Setup.
      </Alert>

      <EnhancedTable
        data={pagedTemplates}
        totalCount={filteredTemplates.length}
        loading={loading}
        columns={columns}
        getRowId={(row) => row.id}
        actions={rowActions}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50]}
        onPageChange={setPage}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(1);
        }}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search templates…"
        onSearchChange={setSearchTerm}
        onRefresh={() => void loadTemplates()}
        onAdd={canManage ? handleOpenCreate : undefined}
        addLabel="New template"
        emptyStateTitle="No scan templates found"
        hidePaginationOnSinglePage
      />

      <WaferImagingTemplateFormDialog
        open={dialogOpen}
        template={editingTemplate}
        onClose={handleCloseDialog}
        onSubmit={handleSave}
        submitting={submitting}
        canEdit={canManage}
      />

      <Dialog open={deleteTarget != null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete scan template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete &quot;{deleteTarget?.name}&quot;? Existing jobs that referenced this template are
            unaffected; new jobs will no longer see it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setDeleteTarget(null)} disabled={submitting}>
            Cancel
          </AppButton>
          <AppButton
            color="error"
            variant="contained"
            disabled={submitting}
            onClick={() => void handleConfirmDelete()}
          >
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </AppListPage>
  );
}
